<template>
  <BaseWidget 
    title="Top bookers"
    :show-info-icon="true"
    info-tooltip="Users ranked by number of bookings within the selected date range."
    :info-tooltip-link="{ url: 'https://support.optixapp.com/en/collections/356500-bookings-assignments', text: 'Learn about Bookings' }"
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :widget-type="props.widgetType"
    :row-span="rowSpan"
    :col-span="colSpan"
    :date-range="dateRange"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="rowSpan >= 2"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <MetricValue 
        :value="topBookerDisplayName"
        :subtitle="dateRangeLabel"
        :has-data-table="rowSpan >= 2 && topBookers.length > 0"
      >
        <template #value="{ value }">
          <div
            v-if="topBooker"
            class="top-booker-value"
            :class="{ clickable: topBooker.account_id }"
            @click="topBooker.account_id && handleBookerClickGuarded(topBooker)"
          >
            <div
              v-if="rowSpan < 2"
              class="top-booker-avatar"
            >
              <img
                v-if="topBooker.avatar"
                :src="topBooker.avatar"
                :alt="topBooker.name"
              >
              <div
                v-else
                class="image-placeholder"
              >
                {{ getInitials(topBooker.name) }}
              </div>
            </div>
            <span class="value">{{ value }}</span>
          </div>
          <span
            v-else
            class="value"
          >{{ value }}</span>
        </template>
        <template
          v-if="hasMore"
          #subtitle-suffix
        >
          <span
            class="focused-view-all-link"
            @click="openViewAllPanel"
          >View all</span>
        </template>
      </MetricValue>
      
      <div
        v-if="!focused && rowSpan >= 2 && topBookers.length > 0"
        class="bookers-list"
      >
        <div
          v-for="(booker, index) in displayBookers"
          :key="booker.userId || index"
          class="booker-item"
          :class="{ clickable: booker.account_id }"
          @click="booker.account_id && handleBookerClickGuarded(booker)"
        >
          <div class="booker-row">
            <div class="user-image">
              <img
                v-if="booker.avatar"
                :src="booker.avatar"
                :alt="booker.name"
              >
              <div
                v-else
                class="image-placeholder"
              >
                {{ getInitials(booker.name) }}
              </div>
            </div>
            <div class="booker-details">
              <div class="booker-name">
                {{ booker.name }}
              </div>
              <div class="booker-meta">
                {{ booker.count }} {{ booker.count === 1 ? 'booking' : 'bookings' }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        v-else-if="!focused && !loading && topBookers.length === 0"
        class="no-data"
      >
        No bookings found
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch, nextTick } from 'vue'
import BaseWidget from './BaseWidget.vue'
import MetricValue from './MetricValue.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useGraphQLData, usePreviewLimit } from '../composables/useWidgetData.js'
import { useLocationFilter } from '../composables/useLocationFilter.js'
import { DATE_RANGE_LABELS, getDateRangeTimestamps } from '../utils/dateRanges.js'
import { useReferenceDate } from '../composables/useReferenceDate.js'
import { isRateLimitError } from '../utils/errorUtils.js'

const bookingLimit = usePreviewLimit(500)
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'
import { pushAdminSidePanel } from '../composables/useOptixPanel.js'
import { getDemoTopBookersData } from '../services/demoData.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

// Initialize dateRange from props, fallback to default
const dateRange = ref(props.props?.dateRange || 'last_30_days')

const dateRangeLabel = computed(() => {
  return DATE_RANGE_LABELS[dateRange.value] || 'Last 30 days'
})

// Reference date for range calculations (can be overridden by parent via postMessage or ?reference_date=)
const referenceDate = useReferenceDate()

// Convert date range to timestamps for API
const timestamps = computed(() => {
  return getDateRangeTimestamps(dateRange.value, referenceDate.value)
})

const locationIds = useLocationFilter()

// Lightweight query for top bookers - only needs owner_account and start_timestamp
const topBookersQuery = `
  query ScheduleNavigationDebounced($location_id: [ID!], $from_timestamp: Int!, $to_timestamp: Int!, $limit: Int, $page: Int) {
    schedule(
      location_id: $location_id
      start_timestamp_to: $to_timestamp
      end_timestamp_from: $from_timestamp
      limit: $limit
      page: $page
      type: BOOKING
    ) {
      total
      data {
        start_timestamp
        owner_account {
          account_id
          name
          avatar {
            url
            __typename
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`

const scheduleVariables = computed(() => {
  // Access timestamps.value to ensure reactivity tracking
  const ts = timestamps.value

  const vars = {
    from_timestamp: ts.from_timestamp,
    to_timestamp: ts.to_timestamp,
    limit: bookingLimit,
    page: 1,
    location_id: locationIds.value.length > 0 ? locationIds.value : undefined
  }
  // Return a new object to ensure watch detects changes
  return { ...vars }
})

const { data, loading, error, cleanup } = useGraphQLData({
  dataKey: 'top_bookers',
  query: topBookersQuery,
  variables: scheduleVariables,
  priority: 2,
  autoFetch: true,
  getDemoData: () => getDemoTopBookersData({
    from_timestamp: timestamps.value.from_timestamp,
    to_timestamp: timestamps.value.to_timestamp
  })
})

// Process bookings data to group by user
const topBookers = computed(() => {
  if (!data.value?.schedule?.data) {
    return []
  }
  
  const allItems = data.value.schedule.data
  const fromTimestamp = timestamps.value.from_timestamp
  const toTimestamp = timestamps.value.to_timestamp
  
  // Filter schedule items
  // The API uses start_timestamp_to and end_timestamp_from which returns items that OVERLAP with the range
  // But for accurate counting, we want items that START within the date range
  // So we always filter by start_timestamp on the client side for accuracy
  const bookingItems = allItems.filter(item => {
    // Require owner_account to identify the booker
    if (!item.owner_account) {
      return false
    }
    
    // Items must have a start_timestamp (required for grouping)
    if (!item.start_timestamp) {
      return false
    }
    
    // Filter by start_timestamp to ensure items start within the date range
    // This ensures accurate counts regardless of API filtering behavior
    return item.start_timestamp >= fromTimestamp && item.start_timestamp <= toTimestamp
  })
  
  // Group bookings by user
  const userMap = new Map()
  
  bookingItems.forEach(item => {
    const account = item.owner_account
    if (!account) return
    
    // Use account_id as primary ID when available, otherwise name (for opening account panel)
    const accountName = account.name || '(Unnamed)'
    const accountId = account.account_id || null
    const userId = accountId ? String(accountId) : accountName
    
    const existing = userMap.get(userId) || {
      userId,
      account_id: accountId,
      name: accountName,
      avatar: account.avatar?.url || null,
      count: 0
    }
    
    userMap.set(userId, {
      ...existing,
      count: existing.count + 1
    })
  })
  
  // Convert to array and sort by count (descending)
  return Array.from(userMap.values())
    .sort((a, b) => b.count - a.count)
})

// Top booker (first in sorted list) for avatar display
const topBooker = computed(() => topBookers.value[0] ?? null)

// Top booker's count (or "0" when no bookers)
const topBookerCount = computed(() => {
  const booker = topBooker.value
  return booker ? booker.count.toString() : '0'
})

// Display top 3 bookers only (fixed, no scrolling)
const displayBookers = computed(() => {
  if (props.rowSpan === 1) {
    return []
  }
  return topBookers.value.slice(0, 3)
})

// Whether there are more bookers beyond the top 3
const hasMore = computed(() => topBookers.value.length > 3)

// Open "View all" in native Optix side panel
const openViewAllPanel = () => {
  pushAdminSidePanel('view-all-top-bookers')
}

// Get initials for placeholder
const getInitials = (name) => {
  if (!name) return '?'
  const words = name.split(' ')
  if (words.length >= 2) {
    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Format name as "FirstName L." (first name + first initial of last name) for main KPI
const formatTopBookerName = (name) => {
  if (!name) return '—'
  const words = name.trim().split(/\s+/)
  if (words.length >= 2) {
    const firstName = words[0]
    const lastInitial = words[words.length - 1].charAt(0).toUpperCase()
    return `${firstName} ${lastInitial}.`
  }
  return words[0] || '—'
}

// Top booker display name for main KPI (first name + last initial)
const topBookerDisplayName = computed(() => {
  const booker = topBooker.value
  if (!booker) return '—'
  return formatTopBookerName(booker.name)
})

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) {}
  }
  if ((!clientId || !canvasUUID) && window.parent && window.parent !== window) {
    try {
      const parentParams = new URLSearchParams(new URL(window.parent.location.href).search)
      if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
    } catch (_e) {}
  }
  return { client_id: clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const handleBookerClickGuarded = guard((booker) => {
  const accountId = booker.account_id
  if (!accountId || (typeof accountId === 'string' && !/^\d+$/.test(accountId))) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(accountId),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (newDateRange) => {
  if (newDateRange && newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    // Don't manually refresh here - let the reactive watch handle it
  }
}, { immediate: true })

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 12px;
}


.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.no-data {
  padding: 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.bookers-list {
  margin-top: 0;
  flex: 1;
  min-width: 0;
  padding: 0 8px;
  overflow: hidden;
}

.booker-item {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  margin: 0 -4px;
  padding: 10px 4px;
  min-height: 64px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.booker-item:first-child {
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.booker-item:last-child {
  border-bottom: none;
}

.booker-item.clickable {
  cursor: pointer;
}

.booker-item.clickable:hover {
  background-color: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
}

.booker-row {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  width: 100%;
}

.booker-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.booker-name {
  font-size: 14px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.booker-meta {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.user-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.user-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}


.top-booker-value {
  display: flex;
  align-items: center;
  gap: 8px;
}

.top-booker-value.clickable {
  cursor: pointer;
}

.top-booker-value.clickable:hover {
  opacity: 0.85;
}

.top-booker-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.top-booker-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.top-booker-value .image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.top-booker-value .value {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
}

.view-all-footer {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  height: 50px;
  padding: 0 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}

.view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  font-weight: 400;
  cursor: pointer;
  transition: color 0.2s ease;
}

.view-all-link:hover {
  color: #2563eb;
}

.focused-view-all-link {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  text-decoration: none;
  cursor: pointer;
  transition: color 0.2s;
  flex-shrink: 0;
}

.focused-view-all-link:hover {
  color: #2563eb;
}
</style>
